import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import ZensioLogo from "@/components/ZensioLogo";
import ThemeToggle from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";

const navLinks = [
  { to: "/funktioner", label: "Funktioner" },
  { to: "/priser", label: "Priser" },
  { to: "/blogg", label: "Blogg" },
  { to: "/om-oss", label: "Om oss" },
  { to: "/kontakt", label: "Kontakt" },
  { to: "/hjalp", label: "Hjälp" },
];

interface PublicNavbarProps {
  /** Extra link shown only on landing page */
  extraLinks?: { href: string; label: string }[];
}

export default function PublicNavbar({ extraLinks }: PublicNavbarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeAnchor, setActiveAnchor] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  const anchorHrefs = (extraLinks ?? [])
    .map((l) => l.href)
    .filter((h) => h.startsWith("#"));

  // Track visible section for active highlighting
  useEffect(() => {
    if (anchorHrefs.length === 0) return;
    const sections = anchorHrefs
      .map((h) => document.getElementById(h.slice(1)))
      .filter((el): el is HTMLElement => !!el);
    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActiveAnchor(`#${visible.target.id}`);
      },
      { rootMargin: "-40% 0px -50% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] },
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, [anchorHrefs.join("|")]);

  // Show sticky mini-CTA after user scrolls past hero
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 480);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleAnchorClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string,
  ) => {
    if (!href.startsWith("#")) return;
    const el = document.getElementById(href.slice(1));
    if (!el) return;
    e.preventDefault();
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    history.replaceState(null, "", href);
    setMobileMenuOpen(false);
    // Move focus to the section for screen readers & keyboard users
    const prevTabIndex = el.getAttribute("tabindex");
    if (prevTabIndex === null) el.setAttribute("tabindex", "-1");
    // Delay to let smooth scroll settle without stealing scroll position
    window.setTimeout(() => {
      el.focus({ preventScroll: true });
    }, 300);
  };

  return (
    <nav className="sticky top-0 z-50 border-b border-foreground/10 bg-background/85 backdrop-blur-xl supports-[backdrop-filter]:bg-background/70">
      <div className="container flex h-16 items-center justify-between gap-3">
        <Link to="/" className="flex shrink-0 items-center gap-2.5">
          <ZensioLogo size={56} />
          <span className="font-display text-xl font-normal tracking-tight text-foreground">Zensio</span>
        </Link>

        <div className="hidden min-w-0 flex-1 items-center justify-center gap-4 xl:gap-6 lg:flex">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`whitespace-nowrap transition-colors font-mono uppercase tracking-[0.12em] text-[11px] ${
                pathname === link.to
                  ? "font-medium text-foreground"
                  : "text-muted-foreground hover:text-secondary"
              }`}
            >
              {link.label}
            </Link>
          ))}
          {extraLinks?.map((link) => {
            const isActive = link.href === activeAnchor;
            return (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleAnchorClick(e, link.href)}
                className={`relative whitespace-nowrap font-mono uppercase tracking-[0.12em] text-[11px] transition-colors ${
                  isActive
                    ? "text-foreground font-medium"
                    : "text-muted-foreground hover:text-secondary"
                }`}
              >
                {link.label}
                {isActive && (
                  <span className="absolute -bottom-1 left-0 right-0 h-px bg-secondary" />
                )}
              </a>
            );
          })}
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          <Button
            size="sm"
            asChild
            className={`cta-base cta-lift cta-on-light hidden lg:inline-flex rounded-none bg-secondary text-secondary-foreground hover:bg-secondary/90 font-normal gap-1.5 ${
              scrolled
                ? "opacity-100 translate-y-0 pointer-events-auto mr-1"
                : "opacity-0 -translate-y-2 pointer-events-none w-0 overflow-hidden px-0 mr-0"
            }`}
            aria-hidden={!scrolled}
          >
            <Link to="/register?trial=true">
              Starta gratis
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </Button>
          <ThemeToggle />
          <Button variant="ghost" size="sm" className="cta-ghost-light hidden lg:inline-flex text-muted-foreground hover:text-foreground font-normal" asChild>
            <Link to="/login">Logga in</Link>
          </Button>
          <Button
            size="sm"
            className={`cta-base cta-lift cta-on-light hidden lg:inline-flex rounded-none bg-secondary text-secondary-foreground hover:bg-secondary/90 font-normal ${
              scrolled ? "opacity-0 pointer-events-none w-0 overflow-hidden px-0" : "opacity-100"
            }`}
            asChild
          >
            <Link to="/register?trial=true">Testa gratis</Link>
          </Button>
          {/* Mobile sticky mini-CTA */}
          <Button
            size="sm"
            asChild
            className={`cta-base cta-on-light lg:hidden rounded-none bg-secondary text-secondary-foreground hover:bg-secondary/90 font-normal text-xs h-8 px-2.5 gap-1 ${
              scrolled ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1 pointer-events-none w-0 overflow-hidden px-0"
            }`}
            aria-hidden={!scrolled}
          >
            <Link to="/register?trial=true">
              Starta gratis
              <ArrowRight className="h-3 w-3" />
            </Link>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden text-foreground"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Stäng meny" : "Öppna meny"}
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="border-t border-border/40 bg-primary lg:hidden max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain">
          <div className="container flex flex-col gap-1 py-4 pb-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileMenuOpen(false)}
                className="rounded-md px-3 py-3 text-base text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
              >
                {link.label}
              </Link>
            ))}
            {extraLinks?.map((link) => {
              const isActive = link.href === activeAnchor;
              return (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleAnchorClick(e, link.href)}
                  className={`rounded-md px-3 py-3 text-base transition-colors ${
                    isActive
                      ? "bg-accent text-foreground font-medium"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground"
                  }`}
                >
                  {link.label}
                </a>
              );
            })}
            <div className="mt-3 flex flex-col gap-2 border-t border-border/40 pt-4">
              <Button variant="outline" className="cta-ghost-light w-full h-12 text-base border-border text-foreground" asChild>
                <Link to="/login">Logga in</Link>
              </Button>
              <Button className="cta-base cta-lift cta-on-light w-full h-12 text-base bg-secondary text-secondary-foreground hover:bg-secondary/90" asChild>
                <Link to="/register?trial=true">Testa gratis</Link>
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
